import { SourcingChannelStatus } from 'api-clients/monolith';
import { Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';

import { SetReachPublisherStatus } from '@/components/pages/channels/campaigns/use-reach-channels';
import { CheckCircleIcon } from '@/components/icons/check-circle';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToggleCampaign } from '@/hooks/use-toggle-campaign';

interface ToggleCampaignProps {
  accessibleContext: { openingName: string; channelName: string };
  sourcingPurchaseId: string;
  reachCampaignId: string | null;
  status: SourcingChannelStatus;
  channel: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
  isDisabled?: boolean;
  openingName?: string;
  openingId?: string;
  startDate?: string | null;
  endDate?: string | null;
  budget?: number | null;
  spent?: number | null;
}

export function ToggleCampaign({
  accessibleContext,
  sourcingPurchaseId,
  reachCampaignId,
  status,
  channel,
  setReachPublisherStatus,
  isDisabled,
  openingName,
  openingId,
  startDate,
  endDate,
  budget,
  spent,
}: ToggleCampaignProps) {
  const intl = useIntl();
  const { handleToggle, isPending, isCampaignActive, isPool } =
    useToggleCampaign({
      sourcingPurchaseId,
      reachCampaignId,
      status,
      channel,
      setReachPublisherStatus,
      openingName,
      openingId,
      startDate,
      endDate,
      budget,
      spent,
    });

  if (isPending) {
    return <Loader2 className="text-fx-primary h-4 animate-spin" />;
  }

  if (isPool)
    return (
      <Button
        aria-label={intl.formatMessage(
          {
            defaultMessage: 'Complete campaign for {opening} on {channel}',
            description:
              'Permanently complete the campaign advertising opening on sourcing channel',
          },
          {
            opening: accessibleContext.openingName,
            channel: accessibleContext.channelName,
          },
        )}
        variant="icon"
        size="icon"
        className="border-fx-neutral-300 border"
        onClick={handleToggle}
        disabled={status !== 'active'} // Only allow completion of active pool campaigns. Disallow activation of pool campaigns.
      >
        <CheckCircleIcon />
      </Button>
    );

  return (
    <>
      <Switch
        aria-label={intl.formatMessage(
          {
            defaultMessage: 'Campaign active for {opening} on {channel}',
            description:
              'Whether the campaign advertising opening on sourcing channel is active',
          },
          {
            opening: accessibleContext.openingName,
            channel: accessibleContext.channelName,
          },
        )}
        checked={isCampaignActive}
        onCheckedChange={handleToggle}
        disabled={isDisabled}
      />
    </>
  );
}
